import { libraryItems as fallbackLibrary, products as fallbackProducts, type Product } from "@digital-garden/shared";
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type ContentItem, type SessionUser } from "./api";

export type DataMode = "loading" | "live" | "fallback";

export interface PublicConfig {
  appEnv: string;
  turnstile: { siteKey: string | null; required: boolean };
  payments: { lemonsqueezyConfigured: boolean };
}

const fallbackConfig: PublicConfig = {
  appEnv: "development",
  turnstile: { siteKey: null, required: false },
  payments: { lemonsqueezyConfigured: false },
};

interface CityContextValue {
  products: Product[];
  content: ContentItem[];
  settings: Record<string, unknown>;
  user: SessionUser | null;
  config: PublicConfig;
  mode: DataMode;
  issue: string | null;
  inboxUnread: number;
  refresh: () => Promise<void>;
  refreshSession: () => Promise<void>;
  refreshInbox: () => Promise<void>;
}

const fallbackContent: ContentItem[] = fallbackLibrary.map((item) => ({
  id: item.id,
  slug: item.slug,
  title: item.title,
  type: item.type.toLowerCase() as ContentItem["type"],
  summary: item.description,
  bodyMarkdown: "",
  access: item.access.toLowerCase() as ContentItem["access"],
  ownershipBasis: "created",
  status: "published",
  readingMinutes: item.readingMinutes,
  tags: [],
  publishedAt: null,
}));

const CityContext = createContext<CityContextValue | undefined>(undefined);

export function CityProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>(fallbackProducts);
  const [content, setContent] = useState<ContentItem[]>(fallbackContent);
  const [settings, setSettings] = useState<Record<string, unknown>>({});
  const [user, setUser] = useState<SessionUser | null>(null);
  const [config, setConfig] = useState<PublicConfig>(fallbackConfig);
  const [mode, setMode] = useState<DataMode>("loading");
  const [issue, setIssue] = useState<string | null>(null);
  const [inboxUnread, setInboxUnread] = useState(0);

  const loadInboxUnread = useCallback(async (sessionUser: SessionUser | null) => {
    if (!sessionUser) { setInboxUnread(0); return; }
    try {
      const inbox = await api<{ unreadCount: number }>("/v1/me/inbox?status=unread");
      setInboxUnread(inbox.unreadCount);
    } catch {
      setInboxUnread(0);
    }
  }, []);

  const refreshSession = useCallback(async () => {
    try {
      const session = await api<{ user: SessionUser | null }>("/v1/session");
      setUser(session.user);
      await loadInboxUnread(session.user);
    } catch {
      setUser(null);
      setInboxUnread(0);
    }
  }, [loadInboxUnread]);

  const refreshInbox = useCallback(async () => {
    await loadInboxUnread(user);
  }, [loadInboxUnread, user]);

  const refresh = useCallback(async () => {
    setMode("loading");
    try {
      const [catalogue, library, publicSettings, session, publicConfig] = await Promise.all([
        api<{ products: Product[] }>("/v1/products"),
        api<{ items: ContentItem[] }>("/v1/content"),
        api<{ settings: Record<string, unknown> }>("/v1/settings/public"),
        api<{ user: SessionUser | null }>("/v1/session"),
        api<PublicConfig>("/v1/config/public"),
      ]);
      setProducts(catalogue.products);
      setContent(library.items);
      setSettings(publicSettings.settings);
      setUser(session.user);
      setConfig(publicConfig);
      await loadInboxUnread(session.user);
      setIssue(null);
      setMode("live");
    } catch (error) {
      setProducts(fallbackProducts);
      setContent(fallbackContent);
      setSettings({});
      setUser(null);
      setConfig(fallbackConfig);
      setInboxUnread(0);
      setIssue(error instanceof Error ? error.message : "API unavailable");
      setMode("fallback");
    }
  }, [loadInboxUnread]);

  useEffect(() => { void refresh(); }, [refresh]);

  const value = useMemo<CityContextValue>(() => ({ products, content, settings, user, config, mode, issue, inboxUnread, refresh, refreshSession, refreshInbox }), [products, content, settings, user, config, mode, issue, inboxUnread, refresh, refreshSession, refreshInbox]);
  return <CityContext.Provider value={value}>{children}</CityContext.Provider>;
}

export function useCity() {
  const context = useContext(CityContext);
  if (!context) throw new Error("useCity must be used inside CityProvider");
  return context;
}
